/**
 * Helpers for user/viewdetails club payload (same field fallbacks as Vibes mobile web).
 */
import {
  PLACEHOLDER_BANNER_URL,
  PLACEHOLDER_LOGO_URL,
} from "../constants/mediaPlaceholders";
export function resolvePublicAssetUrl(pathOrUrl) {
  if (pathOrUrl == null || pathOrUrl === "") return "";
  const s = String(pathOrUrl).trim();
  if (!s) return "";
  if (/^(https?:|data:|blob:)/i.test(s)) return s;
  if (/^\/\//.test(s)) return s;
  const base = (process.env.REACT_APP_BASE_URL || "").replace(/\/$/, "");
  const rel = s.startsWith("/") ? s.slice(1) : s;
  return base ? `${base}/${rel}` : s;
}

export function isApiSuccess(status) {
  return (
    status === true ||
    status === 1 ||
    status === "true" ||
    status === "1"
  );
}

export function pickImageUrl(item) {
  if (!item || typeof item !== "object") return "";
  let raw = "";
  const photos = item.photos;
  if (Array.isArray(photos) && photos.length > 0) {
    const p = photos[0];
    if (typeof p === "string") raw = p;
    else raw = p?.url || p?.uri || "";
  } else {
    const img = item.image;
    if (typeof img === "string") raw = img;
    else if (img && typeof img === "object") {
      raw = img.uri || img.url || "";
    }
  }
  if (!raw) {
    raw =
      item.thumbnail ||
      item.thumb ||
      item.photo ||
      item.imageUrl ||
      item.banner ||
      item.businessBanner ||
      item.boothImage ||
      item.boothPhoto ||
      "";
  }
  if (!raw && Array.isArray(item.booths) && item.booths.length > 0) {
    const firstBooth = item.booths[0] || {};
    raw =
      firstBooth.image ||
      firstBooth.imageUrl ||
      firstBooth.thumbnail ||
      firstBooth.photo ||
      "";
  }
  return raw ? resolvePublicAssetUrl(raw) : "";
}

export function pickTitle(item) {
  if (!item || typeof item !== "object") return "Venue";
  return (
    item.businessName ||
    item.name ||
    item.title ||
    item.clubName ||
    "Venue"
  );
}

export function pickAddress(item) {
  return item.address || item.fullAddress || item.city || "";
}

export function pickId(item) {
  if (!item || typeof item !== "object") return "";
  return String(item._id || item.id || item.eventId || "");
}

export function pickEventsFromClub(data) {
  if (!data || typeof data !== "object") return [];
  const keys = ["events", "eventList", "hostEvents", "upcomingEvents", "event", "tables"];
  for (const k of keys) {
    const v = data[k];
    if (Array.isArray(v)) return v;
  }
  return [];
}

export function pickEventType(ev) {
  if (!ev || typeof ev !== "object") return "";
  return String(ev.type || ev.eventType || ev.category || ev.tableType || "").trim();
}

export function pickRating(item) {
  if (!item || typeof item !== "object") return null;
  const r = item.rating ?? item.avgRating ?? item.stars;
  if (r === undefined || r === null) return null;
  const n = Number(r);
  return Number.isFinite(n) ? n : null;
}

export function pickCapacityText(ev) {
  if (!ev || typeof ev !== "object") return "";
  const c =
    ev.capacity ??
    ev.eventCapacity ??
    ev.tableCapacity ??
    ev.maxGuest ??
    ev.maxGuests;
  const tables = ev.tables ?? ev.tableCount;
  if (c != null && c !== "" && tables) return `Capacity: ${c} · ${tables} tables`;
  if (c != null && c !== "") return `Capacity: ${c}`;
  if (tables) return `${tables} tables`;
  return "";
}

/** Logo / circular image — host profile uses businessPicture. Falls back to local placeholder. */
export function pickLogoUrl(data) {
  if (!data || typeof data !== "object") return PLACEHOLDER_LOGO_URL;
  const raw =
    data.businessPicture ||
    data.profilePicture ||
    data.logo ||
    data.profileImage ||
    data.avatar ||
    data.image?.url ||
    data.image?.uri ||
    "";
  if (typeof raw === "string" && raw.trim()) {
    return resolvePublicAssetUrl(raw.trim());
  }
  const fromPhotos = pickImageUrl(data);
  return fromPhotos || PLACEHOLDER_LOGO_URL;
}

/** Hero banner — host profile uses businessBanner. Falls back to local placeholder. */
export function pickBannerUrl(data) {
  if (!data || typeof data !== "object") return PLACEHOLDER_BANNER_URL;
  const raw = data.businessBanner || data.banner || "";
  if (typeof raw === "string" && raw.trim()) {
    return resolvePublicAssetUrl(raw.trim());
  }
  const fallback = pickImageUrl(data);
  return fallback || PLACEHOLDER_BANNER_URL;
}

/** URLs for club hero slider: banner + gallery photos (deduped). */
export function pickBannerSlideUrls(data) {
  if (!data || typeof data !== "object") return [PLACEHOLDER_BANNER_URL];
  const seen = new Set();
  const out = [];

  const push = (u) => {
    if (!u || typeof u !== "string") return;
    const s = u.trim();
    if (!s || seen.has(s)) return;
    seen.add(s);
    out.push(s);
  };

  const rawBanner = data.businessBanner || data.banner || "";
  if (typeof rawBanner === "string" && rawBanner.trim()) {
    push(resolvePublicAssetUrl(rawBanner.trim()));
  }

  const photos = data.photos;
  if (Array.isArray(photos)) {
    for (const p of photos) {
      const raw = typeof p === "string" ? p : p?.url || p?.uri || "";
      if (raw) push(resolvePublicAssetUrl(String(raw).trim()));
    }
  }

  if (!out.length) {
    const fb = pickImageUrl(data);
    if (fb) push(fb);
  }
  if (!out.length) out.push(PLACEHOLDER_BANNER_URL);
  return out;
}

/** Text after "Capacity:" for compact display. */
export function capacityValueOnly(ev) {
  const line = pickCapacityText(ev);
  if (!line) return "";
  return line.replace(/^Capacity:\s*/i, "").trim() || line;
}

/** Normalize API `type` (e.g. "Club", "VIP Entry") for tab matching. */
export function normalizeEventType(ev) {
  return pickEventType(ev).toLowerCase().trim();
}

function isTruthyFlag(value) {
  if (typeof value === "boolean") return value;
  const normalized = String(value || "").trim().toLowerCase();
  return normalized === "true" || normalized === "1" || normalized === "yes";
}

export function isBottleServiceEvent(ev) {
  if (!ev || typeof ev !== "object") return false;
  const typeMatches = [
    ev.type,
    ev.eventType,
    ev.category,
    ev.tableType,
    ev.serviceType,
    ev.subType,
  ]
    .map((v) => String(v || "").trim().toLowerCase())
    .some((value) => value === "bottle service" || value === "bottleservice" || value === "bottle");
  if (typeMatches) return true;
  return (
    isTruthyFlag(ev.bottleService) ||
    isTruthyFlag(ev.isBottleService) ||
    isTruthyFlag(ev.hasBottleService) ||
    isTruthyFlag(ev.bottle_service)
  );
}

export function getBottleServiceHeroImageUrl() {
  const raw = String(process.env.REACT_APP_BOTTLE_SERVICE_HERO_IMAGE || "").trim();
  if (raw) return resolvePublicAssetUrl(raw);
  const base = (process.env.PUBLIC_URL || "").replace(/\/$/, "");
  return `${base}/assets/images/bottle-service-banner.png`;
}

/**
 * Filter by tab vs API `events[].type`: Club, Booth, Event, Table, VIP / VIP Entry.
 */
export function eventMatchesCategoryTab(ev, tabKey) {
  const t = normalizeEventType(ev);
  if (tabKey === "club") return t === "club" || t === "table" || isBottleServiceEvent(ev);
  if (tabKey === "booth") return t === "booth";
  if (tabKey === "event") return t === "event";
  if (tabKey === "table") return t === "table";
  if (tabKey === "vip") {
    return t === "vip" || t.includes("vip");
  }
  return false;
}
